import { FC, useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  fetchDefaultFilters,
  setIsDetectedLocation,
} from "../../store/modules/dashboard";
import Modal from "@albertsons/uds/molecule/Modal";
import Button from "@albertsons/uds/molecule/Button";
import { checkLocationPermission, getLocation } from "../../utils/permissions";
import { LocationCoords } from "../../types/permission.types";
import { FilterType } from "../../types/filters.types";

interface DetectLocationProps {
  children: JSX.Element;
  loadFilters?: FilterType[];
  setIsDetectedLocation: (hasAccess: boolean) => void;
  fetchDefaultFilters: (
    location: LocationCoords,
    extraFilters: FilterType[]
  ) => void;
}

const RETRIES_ALLOWED = 2;

const DetectLocation: FC<DetectLocationProps> = (props) => {
  const {
    children,
    loadFilters = [],
    setIsDetectedLocation,
    fetchDefaultFilters,
  } = props;

  const [isShowLocationError, setIsShowLocationError] = useState(false);
  const [
    isShowLocationSettingChangeSteps,
    setIsShowLocationSettingChangeSteps,
  ] = useState(false);
  const [isShowLocationNotAvailableError, setIsShowLocationNotAvailableError] =
    useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const isInitializedRef = useRef(false);

  const onDecline = (action: (value: boolean) => void) => {
    const locationDeniedAt = new Date();
    const lastLocationDeniedAt = localStorage.getItem("locationDeniedAt");
    if (lastLocationDeniedAt) {
      const lastDeniedDate = new Date(lastLocationDeniedAt);
      if (
        locationDeniedAt.getTime() - lastDeniedDate.getTime() >=
        1000 * 60 * 60 * 24 // 24 hours
      ) {
        action(true);
      }
    } else {
      action(true);
    }
  };

  const onRetry = () => {
    if (retryCount >= RETRIES_ALLOWED - 1) {
      localStorage.setItem("locationDeniedAt", new Date().toISOString());
    } else {
      localStorage.removeItem("locationDeniedAt");
    }
    setRetryCount((prev) => prev + 1);
    checkLocationPermission().then((result) => {
      if (result === "denied") {
        setTimeout(() => {
          setIsShowLocationSettingChangeSteps(true);
          setIsShowLocationError(true);
        }, 1000);
      }
      if (result === "granted" || result === "prompt") {
        getLocation()
          .then((location) => {
            localStorage.removeItem("locationDeniedAt");
            fetchDefaultFilters(location, loadFilters);
          })
          .catch((error) => {
            if (error.code === 2) {
              // TIMEOUT error
              setIsShowLocationNotAvailableError(true);
            } else {
              setIsShowLocationSettingChangeSteps(true);
              setIsShowLocationError(true);
            }
          });
      }
    });
  };

  const onContinue = () => {
    localStorage.setItem("locationDeniedAt", new Date().toISOString());
  };

  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      checkLocationPermission().then((result) => {
        if (result === "denied") {
          onDecline(setIsShowLocationError);
        }
        if (result === "denied" || result === "prompt") {
          setIsDetectedLocation(true);
        }
        if (result === "granted" || result === "prompt") {
          getLocation()
            .then((location) => {
              localStorage.removeItem("locationDeniedAt");
              fetchDefaultFilters(location, loadFilters);
            })
            .catch((error) => {
              if (error.code === 2) {
                // TIMEOUT error
                onDecline(setIsShowLocationNotAvailableError);
              } else {
                onDecline(setIsShowLocationError);
              }
              setIsDetectedLocation(true);
            });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal
        isOpen={isShowLocationError}
        onClose={() => setIsShowLocationError(false)}
        minHeight={20}
        minWidth={20}
        width={350}
        maxWidth="calc(100vw - 60px)"
        className="bg-white overflow-hidden !rounded-xl"
      >
        {({ close }) => (
          <div data-testid="location-access-denied-modal">
            <div
              className={clsx(
                "text-lg text-center p-4",
                retryCount >= RETRIES_ALLOWED ? "font-medium text-red-400" : ""
              )}
            >
              {isShowLocationSettingChangeSteps
                ? retryCount >= RETRIES_ALLOWED
                  ? "Please allow location permission from browser settings, and refresh this page."
                  : "Please allow location permission from browser settings."
                : "You need to allow location access or manually select store!"}
            </div>
            <span className="flex items-center pt-3.5">
              {retryCount < RETRIES_ALLOWED ? (
                <Button
                  data-testid="retrybutton"
                  fixed
                  variant="tertiary"
                  className="w-full text-lg py-4 !h-12 border-t border-x-0 border-b-0 border-gray-200 hover:border-gray-200 focus:border-gray-300 hover:bg-gray-100 active:bg-gray-100 focus:outline-none rounded-none"
                  onClick={() => {
                    onRetry();
                    close();
                  }}
                >
                  Retry
                </Button>
              ) : null}
              <Button
                data-testid="continuebutton"
                fixed
                variant="tertiary"
                className="w-full text-lg py-4 !h-12 border-t border-l border-r-0 border-b-0 border-gray-200 hover:border-gray-200 focus:border-gray-300 hover:bg-gray-100 active:bg-gray-100 focus:outline-none rounded-none"
                onClick={() => {
                  onContinue();
                  close();
                }}
              >
                Continue
              </Button>
            </span>
          </div>
        )}
      </Modal>
      <Modal
        isOpen={isShowLocationNotAvailableError}
        onClose={() => setIsShowLocationNotAvailableError(false)}
        minHeight={20}
        minWidth={20}
        width={350}
        maxWidth="calc(100vw - 60px)"
        className="bg-white overflow-hidden !rounded-xl"
      >
        {({ close }) => (
          <div data-testid="location-not-available-modal">
            <div className="text-lg text-center p-4">
              Your location is not available, kindly manually select store!
            </div>
            <span className="flex items-center pt-3.5">
              <Button
                data-testid="continuebutton"
                fixed
                variant="tertiary"
                className="w-full text-lg py-4 !h-12 border-t border-l border-r-0 border-b-0 border-gray-200 hover:border-gray-200 focus:border-gray-300 hover:bg-gray-100 active:bg-gray-100 focus:outline-none rounded-none"
                onClick={() => {
                  onContinue();
                  close();
                }}
              >
                Continue
              </Button>
            </span>
          </div>
        )}
      </Modal>
      {children}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setIsDetectedLocation: (hasAccess: boolean) =>
    dispatch(setIsDetectedLocation(hasAccess)),
  fetchDefaultFilters: (
    location: LocationCoords,
    extraFilters: FilterType[] = []
  ) => dispatch(fetchDefaultFilters(location, extraFilters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DetectLocation);
